import { useCallback, useEffect, useState } from "react";
import { track } from "@/lib/analytics";
import {
  createEntry,
  loadEntries,
  saveEntries,
  sortEntries,
  parseDateFound,
  toText,
  type BiteEntry,
  type BiteEntryInput,
} from "@/lib/bite-log";
import { deletePhoto, getPhoto, putPhoto } from "@/lib/photo-store";
import { getIdProfile } from "@/lib/tick-id";
import { CARD_SURFACE } from "@/components/page-shell";
import { PrintButton } from "@/components/print-button";
import { cn } from "@/lib/utils";
import { RemovalSteps } from "./removal-steps";
import { BiteRecord } from "./bite-record";
import { WatchTimeline } from "./watch-timeline";
import { BiteSummaryPanel } from "./bite-summary";

type Step = "remove" | "record" | "watch";

const STEP_LABELS: { id: Step; label: string }[] = [
  { id: "remove", label: "Remove it" },
  { id: "record", label: "Write it down" },
  { id: "watch", label: "Watch" },
];

const OUTLINE_BUTTON =
  "inline-flex min-h-11 items-center rounded-sm border border-foreground/30 px-5 py-3 text-sm font-semibold text-foreground transition-colors hover:border-foreground";

/** "2026-10-04" -> "October 4, 2026". Falls back to what was stored. */
function longDate(iso: string): string {
  const d = parseDateFound(iso);
  return d
    ? d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })
    : iso;
}

/**
 * The bite flow.
 *
 * The page this replaces was a static array of six cards in a grid: no state, no
 * branching, no questions, and a log that nothing ever read back. This is the same
 * advice as a sequence you move through, ending in a timeline built from the date you
 * entered — which is the first thing on the site that reads the log at all.
 *
 * If a bite is already logged, the flow opens on the timeline rather than restarting at
 * step one. Coming back is the common case: the watching lasts weeks, the removal takes
 * a minute.
 */
export function BiteFlow({ lead }: { lead?: React.ReactNode }) {
  const [entries, setEntries] = useState<BiteEntry[]>([]);
  const [ready, setReady] = useState(false);
  const [step, setStep] = useState<Step>("remove");
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  useEffect(() => {
    const stored = sortEntries(loadEntries());
    setEntries(stored);
    if (stored.length > 0) setStep("watch");
    setReady(true);
  }, []);

  const go = useCallback((next: Step) => {
    setStep(next);
    track("bite_flow_step", { step: next });
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const save = useCallback(
    async (input: BiteEntryInput, photo?: File) => {
      const photoId = photo ? await putPhoto(photo) : undefined;
      const entry = createEntry({ ...input, photoId });
      setEntries((prev) => {
        const next = sortEntries([entry, ...prev]);
        saveEntries(next);
        return next;
      });
      track("bite_log_saved", {
        hasPhoto: Boolean(photoId),
        hasSpecies: Boolean(input.speciesSlug),
        hasLocation: Boolean(input.exposureStateAbbr),
      });
      go("watch");
    },
    [go],
  );

  const remove = useCallback((entry: BiteEntry) => {
    if (entry.photoId) void deletePhoto(entry.photoId);
    setEntries((prev) => {
      const next = prev.filter((e) => e.id !== entry.id);
      saveEntries(next);
      return next;
    });
    setConfirmDelete(null);
    track("bite_log_deleted", {});
  }, []);

  const download = useCallback(() => {
    const text = toText(entries, (slug) => getIdProfile(slug)?.commonName ?? slug);
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "tick-bite-log.txt";
    a.click();
    URL.revokeObjectURL(url);
    track("bite_log_exported", { entries: entries.length });
  }, [entries]);

  const latest = entries[0];

  return (
    // The steps take the reading column; on a wide screen the right-hand column holds a
    // summary of this bite that stays in view while the steps scroll past. It prints on
    // its own, and first, when the page is printed.
    <div className="grid gap-x-10 gap-y-8 lg:grid-cols-[minmax(0,1fr)_17rem]">
      <div data-print-region="flow" className="min-w-0 max-w-3xl space-y-8">
        {lead}
        <Stepper current={step} onSelect={go} hasEntries={entries.length > 0} />

        {step === "remove" && <RemovalSteps onDone={() => go("record")} />}

        {step === "record" && <BiteRecord onSave={save} onCancel={() => go("remove")} />}

        {step === "watch" && ready && (
          <div className="space-y-10">
            {latest ? (
              <>
                <LoggedBite
                  entry={latest}
                  deleteControl={
                    <DeleteRow
                      entry={latest}
                      confirmDelete={confirmDelete}
                      setConfirmDelete={setConfirmDelete}
                      onDelete={remove}
                      label="Delete this bite"
                    />
                  }
                />
                <WatchTimeline
                  bite={parseDateFound(latest.dateFound) ?? new Date()}
                  speciesSlug={latest.speciesSlug}
                />
                <div className="no-print flex flex-wrap items-center gap-3">
                  <PrintButton page="tick-bite-tool" label="Print or save as PDF" />
                  <button type="button" onClick={download} className={OUTLINE_BUTTON}>
                    Download as text
                  </button>
                  <button type="button" onClick={() => go("record")} className={OUTLINE_BUTTON}>
                    Log another bite
                  </button>
                </div>
                {entries.length > 1 && (
                  <EarlierBites
                    entries={entries.slice(1)}
                    confirmDelete={confirmDelete}
                    setConfirmDelete={setConfirmDelete}
                    onDelete={remove}
                  />
                )}
              </>
            ) : (
              <p className="text-[0.9375rem] text-muted-foreground">
                Nothing logged yet.{" "}
                <button type="button" onClick={() => go("record")} className="link font-semibold">
                  Write down a bite
                </button>{" "}
                and this becomes a timeline.
              </p>
            )}
          </div>
        )}
      </div>

      <aside
        aria-label="Summary of this bite"
        className="hidden lg:sticky lg:top-24 lg:block lg:self-start print:order-first print:block"
      >
        <BiteSummaryPanel entry={latest} ready={ready} onLog={() => go("record")} />
      </aside>
    </div>
  );
}

/**
 * Where you are in the three steps, as a segmented rule: a bar over each step, the
 * current one drawn in ink with its numeral in rust. Was a row of rounded pills joined
 * by arrows, which wrapped "Watch" onto a line of its own on a phone.
 */
function Stepper({
  current,
  onSelect,
  hasEntries,
}: {
  current: Step;
  onSelect: (s: Step) => void;
  hasEntries: boolean;
}) {
  const at = STEP_LABELS.findIndex((s) => s.id === current);
  return (
    <nav aria-label="Bite steps" className="no-print">
      <ol className="grid grid-cols-3 gap-x-2 sm:gap-x-4">
        {STEP_LABELS.map((s, i) => {
          const isCurrent = i === at;
          const reachable = s.id !== "watch" || hasEntries;
          return (
            <li key={s.id} className="min-w-0">
              <button
                type="button"
                onClick={() => reachable && onSelect(s.id)}
                aria-current={isCurrent ? "step" : undefined}
                disabled={!reachable}
                className={cn(
                  "flex min-h-12 w-full flex-col items-start gap-1 border-t-[3px] pt-2 text-left text-sm font-semibold leading-tight transition-colors sm:flex-row sm:items-baseline sm:gap-2",
                  isCurrent && "border-foreground text-foreground",
                  !isCurrent && i < at && "border-foreground/35 text-muted-foreground",
                  !isCurrent && i > at && "border-border text-muted-foreground",
                  !isCurrent && reachable && "hover:border-foreground/60 hover:text-foreground",
                  !reachable && "cursor-not-allowed opacity-55",
                )}
              >
                <span
                  aria-hidden
                  className={cn(
                    "tabular font-display text-lg font-normal leading-none",
                    isCurrent && "text-rust-ink",
                  )}
                >
                  {i + 1}
                </span>
                {s.label}
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function LoggedBite({
  entry,
  deleteControl,
}: {
  entry: BiteEntry;
  deleteControl: React.ReactNode;
}) {
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const species = entry.speciesSlug ? getIdProfile(entry.speciesSlug) : undefined;

  useEffect(() => {
    let url: string | null = null;
    let cancelled = false;
    if (entry.photoId) {
      void getPhoto(entry.photoId).then((blob) => {
        if (!blob || cancelled) return;
        url = URL.createObjectURL(blob);
        setPhotoUrl(url);
      });
    }
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [entry.photoId]);

  return (
    <section
      aria-labelledby="logged-bite"
      className={cn(CARD_SURFACE, "px-5 py-5 sm:px-7 sm:py-6")}
    >
      <div className="flex items-start justify-between gap-4">
        <h2
          id="logged-bite"
          className="font-display text-[1.375rem] font-medium leading-tight text-foreground"
        >
          Your logged bite
        </h2>
        {photoUrl && (
          <img
            src={photoUrl}
            alt="The tick you photographed"
            className="h-16 w-16 shrink-0 rounded-sm border border-border object-cover sm:h-20 sm:w-20"
          />
        )}
      </div>
      <dl className="mt-4 divide-y divide-border border-y border-border text-[0.9375rem]">
        <Row label="Found">{longDate(entry.dateFound)}</Row>
        {entry.bodyLocation && <Row label="On the body">{entry.bodyLocation}</Row>}
        {entry.exposure && <Row label="Picked up">{entry.exposure}</Row>}
        {species && <Row label="Tick">{species.commonName}</Row>}
        {entry.symptoms && <Row label="Notes">{entry.symptoms}</Row>}
      </dl>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
        <p className="text-sm text-muted-foreground">Stored on this device only. Never uploaded.</p>
        {deleteControl}
      </div>
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[6.5rem_1fr] gap-x-3 py-2.5 sm:grid-cols-[8rem_1fr]">
      <dt className="text-sm leading-6 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words font-medium leading-6 text-foreground">{children}</dd>
    </div>
  );
}

function EarlierBites({
  entries,
  confirmDelete,
  setConfirmDelete,
  onDelete,
}: {
  entries: BiteEntry[];
  confirmDelete: string | null;
  setConfirmDelete: (id: string | null) => void;
  onDelete: (e: BiteEntry) => void;
}) {
  return (
    <section aria-labelledby="earlier-bites" className="break-inside-avoid">
      <h2 id="earlier-bites" className="font-display text-xl font-medium text-foreground">
        Earlier bites
      </h2>
      <ul className="mt-3 divide-y divide-border border-y border-border">
        {entries.map((entry) => (
          <li key={entry.id} className="flex flex-wrap items-baseline gap-x-4 gap-y-1 py-3">
            <span className="tabular text-[0.9375rem] font-semibold text-foreground">
              {longDate(entry.dateFound)}
            </span>
            {entry.bodyLocation && (
              <span className="text-sm text-muted-foreground">{entry.bodyLocation}</span>
            )}
            <span className="ml-auto">
              <DeleteRow
                entry={entry}
                confirmDelete={confirmDelete}
                setConfirmDelete={setConfirmDelete}
                onDelete={onDelete}
                label="Delete"
                ariaLabel={`Delete the ${longDate(entry.dateFound)} bite`}
              />
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function DeleteRow({
  entry,
  confirmDelete,
  setConfirmDelete,
  onDelete,
  label,
  ariaLabel,
}: {
  entry: BiteEntry;
  confirmDelete: string | null;
  setConfirmDelete: (id: string | null) => void;
  onDelete: (e: BiteEntry) => void;
  label: string;
  ariaLabel?: string;
}) {
  if (confirmDelete === entry.id) {
    return (
      <span className="no-print inline-flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="text-sm text-foreground">
          Delete the {longDate(entry.dateFound)} bite for good?
        </span>
        <button
          type="button"
          onClick={() => onDelete(entry)}
          className="inline-flex min-h-10 items-center rounded-sm bg-destructive px-4 py-2 text-sm font-semibold text-destructive-foreground"
        >
          Yes, delete
        </button>
        <button
          type="button"
          onClick={() => setConfirmDelete(null)}
          className="inline-flex min-h-10 items-center rounded-sm border border-foreground/30 px-4 py-2 text-sm font-semibold text-foreground hover:border-foreground"
        >
          Keep it
        </button>
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={() => setConfirmDelete(entry.id)}
      aria-label={ariaLabel}
      className="no-print min-h-10 text-sm font-semibold text-muted-foreground underline decoration-border underline-offset-4 transition-colors hover:text-destructive hover:decoration-current"
    >
      {label}
    </button>
  );
}
